import { createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'
import { z } from 'zod'

import { useCreatePointOfSale } from '@/api/api-mutations'
import {
	preloadListPointOfSaleGroups,
	preloadListSalesChannels,
	useListPointOfSaleGroups,
	useListSalesChannels
} from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { Text } from '@/components/base/text'
import { toast, useCreatedToast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

const createPOSSearchSchema = z.object({
	salesChannelUuid: z.string().optional()
})

export const Route = createFileRoute('/_authenticated/settings/points-of-sale/create')({
	component: RouteComponent,
	validateSearch: createPOSSearchSchema,
	loader: async ({ context }) => {
		await preloadListSalesChannels(context, {})
		await preloadListPointOfSaleGroups(context, {})
	},
	staticData: {
		crumb: ({ context: { L } }) => L.General.Create()
	}
})

function RouteComponent() {
	return (
		<RouteModal asDrawer>
			<NewPointOfSaleForm />
		</RouteModal>
	)
}

function NewPointOfSaleForm() {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const createdToast = useCreatedToast()
	const { salesChannelUuid } = Route.useSearch()

	const createPointOfSale = useCreatePointOfSale()
	const salesChannels = useListSalesChannels()

	const salesChannelOptions = useMemo(
		() =>
			salesChannels.data?.map(channel => ({
				label: channel.name,
				value: channel.uuid
			})) || [],
		[salesChannels.data]
	)

	const pointOfSaleGroups = useListPointOfSaleGroups()
	const groupOptions = useMemo(
		() => [
			{ value: '', label: L.Settings.PointOfSale.GroupNone() },
			...(pointOfSaleGroups.data ?? []).map(group => ({ value: group.uuid, label: group.name }))
		],
		[L, pointOfSaleGroups.data]
	)

	const form = useAppForm({
		defaultValues: {
			name: '',
			identifier: '',
			salesChannelUuids: salesChannelUuid ? [salesChannelUuid] : ([] as string[]),
			pointOfSaleGroupUuid: ''
		},
		onSubmit: async ({ value }) => {
			const result = await createPointOfSale({
				body: {
					name: value.name,
					identifier: value.identifier,
					salesChannelUuids: value.salesChannelUuids,
					pointOfSaleGroupUuid: value.pointOfSaleGroupUuid || undefined
				}
			})
			if (result.isSuccess) {
				createdToast(L.Settings.PointOfSale.CreateSuccess(), {
					to: '/settings/points-of-sale/$pointOfSaleUuid',
					params: { pointOfSaleUuid: result.response.uuid }
				})
				handleSuccess()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Settings.PointOfSale.CreateTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='name'
							children={field => (
								<field.TextField
									label={L.Fields.Name()}
									placeholder={L.Settings.PointOfSale.Create.NamePlaceholder()}
								/>
							)}
						/>
					</FormFieldGroup>

					<FormFieldGroup>
						<form.AppField
							name='identifier'
							children={field => (
								<field.TextField
									label={L.Settings.PointOfSale.Column.Identifier()}
									placeholder={L.Settings.PointOfSale.Create.IdentifierPlaceholder()}
									optional
								/>
							)}
						/>
					</FormFieldGroup>

					<FormFieldGroup>
						<form.AppField
							name='salesChannelUuids'
							children={field => (
								<field.ComboboxField
									label={L.Fields.SalesChannel()}
									placeholder={L.Settings.PointOfSale.Create.SelectSalesChannels()}
									multiple
									options={salesChannelOptions}
									optionsLoading={salesChannels.isLoading}
								/>
							)}
						/>
					</FormFieldGroup>

					<FormFieldGroup>
						<form.AppField
							name='pointOfSaleGroupUuid'
							children={field => (
								<field.SelectField
									label={L.Settings.PointOfSale.Column.Group()}
									options={groupOptions}
									optionsLoading={pointOfSaleGroups.isLoading}
								/>
							)}
						/>
						<Text size='small' className='text-ui-fg-muted'>
							{L.Settings.PointOfSale.GroupOptionalHint()}
						</Text>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.CreateButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
